import driveWordmark from "~/assets/products/hero/drive-wordmark.svg";
import type {
  ProductAllFeaturesContent,
  ProductFeatureCategory,
  ProductHighlightsContent,
  ProductPageContent,
} from "./types";
import { AMAZON_STORE_URL } from "~/lib/external-links";
import { imageUrl } from "~/lib/image-url";

const personaFamily = imageUrl("/images/products/persona/family.jpg");
const personaConnectedTech = imageUrl("/images/products/persona/connected-tech.jpg");
const personaFleetManagement = imageUrl("/images/products/persona/fleet-management.jpg");
const personaRentalOperations = imageUrl("/images/products/persona/rental-operations.jpg");
const personaServiceBusiness = imageUrl("/images/products/persona/service-business.jpg");
const personaPublicSector = imageUrl("/images/products/persona/public-sector.jpg");

const deviceExploded = imageUrl("/images/products/specs/device-exploded.png");
// Plain public/ path, not a JS import — Picture needs real .avif/.webp siblings on disk.
const crossSellView = imageUrl("/images/products/view-device.png");
const crossSellGo = imageUrl("/images/products/cross-sell/go.png");
const driveBoxDesktop = imageUrl("/images/products/last-chance/drive-box-desktop.png");
const driveBoxMobile = imageUrl("/images/products/last-chance/drive-box-mobile.png");
const allFeaturesShopBanner = imageUrl("/images/products/all-features/MiddleAgedCoupleHome-Phone6.png");

function featureCategory(key: string, cardCount: number, icon?: string): ProductFeatureCategory {
  return {
    label: `drive.features.categories.${key}.label`,
    cards: Array.from({ length: cardCount }, (_, i) => ({
      title: `drive.features.categories.${key}.cards.${i}.title`,
      description: `drive.features.categories.${key}.cards.${i}.description`,
    })),
    icon,
  };
}

// Version B of the "Discover what Bouncie Drive can do." section (Figma node
// 7981:61467) — a card-carousel treatment, vs. the accordion layout (node
// 7981:60932, "highlights") that the PDP renders via HighlightsAccordion.
// Hung off DRIVE_PRODUCT.highlightsVariantB below so ProductPdpTemplate can
// switch on the `drive_highlights` assignment; still exported standalone because
// app/routes/dev.highlights-ab.tsx renders both arms side by side directly.
export const DRIVE_HIGHLIGHTS_VARIANT_B: ProductHighlightsContent = {
  eyebrow: "drive.highlightsB.eyebrow",
  heading: "drive.highlightsB.heading",
  description: "drive.highlightsB.description",
  items: [
    { title: "drive.highlightsB.items.liveLocation.title", description: "drive.highlightsB.items.liveLocation.description" },
    { title: "drive.highlightsB.items.drivingHabits.title", description: "drive.highlightsB.items.drivingHabits.description", descriptionLines: 3 },
    { title: "drive.highlightsB.items.geoZones.title", description: "drive.highlightsB.items.geoZones.description" },
    { title: "drive.highlightsB.items.impactDetection.title", description: "drive.highlightsB.items.impactDetection.description" },
    { title: "drive.highlightsB.items.vehicleHealth.title", description: "drive.highlightsB.items.vehicleHealth.description" },
  ],
};

// Copy fields hold i18next keys (see products.json's "drive" namespace), not literal text.
// rating/reviewCount/appRatings/reviews are null/empty until products.drive.tsx's loader
// fills them in with live mktapi/Yotpo data — never a made-up placeholder.
export const DRIVE_PRODUCT: ProductPageContent = {
  slug: "drive",
  productCode: "bouncieobd",
  name: "Drive",
  metaTitle: "drive.metaTitle",
  metaDescription: "drive.metaDescription",
  hero: {
    images: [
      { src: imageUrl("/images/products/hero/drive-box.png"), alt: "drive.hero.imageAlt" },
      { src: imageUrl("/images/drive-product-image-2.png"), alt: "drive.hero.imageAlt" },
      { src: imageUrl("/images/drive-product-image-3.png"), alt: "drive.hero.imageAlt" },
      { src: imageUrl("/images/drive-product-image-4.png"), alt: "drive.hero.imageAlt" },
    ],
    rating: null,
    reviewCount: null,
    tagline: "drive.hero.tagline",
    productDescriptor: "drive.hero.productDescriptor",
    ctaLabel: "drive.hero.ctaLabel",
    secondaryCtaLabel: "drive.hero.secondaryCtaLabel",
    secondaryCtaHref: AMAZON_STORE_URL,
    wordmark: driveWordmark,
    subscription: {
      headline: "drive.hero.subscription.headline",
      body: "drive.hero.subscription.body",
    },
    featureHighlights: [
      { icon: "location", label: "drive.hero.featureHighlights.location" },
      { icon: "insights", label: "drive.hero.featureHighlights.insights" },
      { icon: "route", label: "drive.hero.featureHighlights.route" },
      { icon: "geoZones", label: "drive.hero.featureHighlights.geoZones" },
      { icon: "alerts", label: "drive.hero.featureHighlights.alerts" },
      { icon: "speed", label: "drive.hero.featureHighlights.speed" },
    ],
    valueProps: [
      "drive.hero.valueProps.shipping",
      "drive.hero.valueProps.returns",
      "drive.hero.valueProps.noFees",
    ],
  },
  obdCompatibilityChecker: true,
  // Figma's "Feature Highlights.vB" (accordion layout) — its own content is still
  // placeholder ("EYEBROW"/"Header"/"Feature Title"×4) upstream in Figma, but the
  // eyebrow/heading/description below are real copy already confirmed for Drive.
  highlights: {
    eyebrow: "drive.highlights.eyebrow",
    heading: "drive.highlights.heading",
    description: "drive.highlights.description",
    items: [
      { title: "drive.highlights.items.location.title", description: "drive.highlights.items.location.description" },
      { title: "drive.highlights.items.safety.title", description: "drive.highlights.items.safety.description" },
      { title: "drive.highlights.items.drivingInsights.title", description: "drive.highlights.items.drivingInsights.description" },
      { title: "drive.highlights.items.vehicleHealth.title", description: "drive.highlights.items.vehicleHealth.description" },
    ],
  },
  // The competing arm of the `drive_highlights` experiment — see registry.ts.
  highlightsVariantB: DRIVE_HIGHLIGHTS_VARIANT_B,
  // All 6 categories now match Figma's finished B2C content, none of which has a card
  // icon — copy for categories beyond GPS & Routes is still being confirmed against
  // Figma one at a time, but the icon removal applies across the board.
  features: {
    eyebrow: "drive.features.eyebrow",
    headline: "drive.features.headline",
    description: "drive.features.description",
    ctaLabel: "drive.features.cta",
    categories: [
      featureCategory("gps_routes", 6),
      featureCategory("driving_insights", 6),
      featureCategory("safety_security", 6),
      featureCategory("diagnostics_care", 6),
      featureCategory("access_users", 3),
    ],
  },
  // Code Connect annotates the button as "Learn More -> Fleet page" — links to the
  // existing /fleet route.
  fleetBanner: {
    headline: "drive.fleetBanner.headline",
    description: "drive.fleetBanner.description",
    ctaLabel: "drive.fleetBanner.ctaLabel",
    ariaLabel: "drive.fleetBanner.ariaLabel",
    ctaHref: "/fleet",
  },
  // Order matches Figma's Filter Row (node 7279:54379) — the most reliable ordering
  // source in the updated Persona Section/Interaction file, since the accordion demo
  // rows themselves shuffle order inconsistently across duplicated frames. Family
  // (index 0) is expanded first and the section auto-cycles from there — see
  // PersonaSection's DEFAULT_EXPANDED_INDEX.
  persona: {
    eyebrow: "drive.persona.eyebrow",
    headline: "drive.persona.headline",
    items: [
      {
        eyebrow: "drive.persona.items.family.eyebrow",
        title: "drive.persona.items.family.title",
        description: "drive.persona.items.family.description",
        ctaLabel: "drive.persona.items.learnMore",
        image: personaFamily,
        imageAlt: "drive.persona.items.family.imageAlt",
        imagePositionDesktop: "34% 50%",
        imagePositionDesktopCollapsed: "33.4% 50%",
        imagePositionMobile: "50% 27.5%",
        imagePositionMobileCollapsed: "50% 23%",
      },
      {
        eyebrow: "drive.persona.items.connectedTech.eyebrow",
        title: "drive.persona.items.connectedTech.title",
        description: "drive.persona.items.connectedTech.description",
        ctaLabel: "drive.persona.items.learnMore",
        image: personaConnectedTech,
        imageAlt: "drive.persona.items.connectedTech.imageAlt",
        // Figma's expanded crop also zooms in slightly (108.96% of the card's height).
        imagePositionDesktop: "47.5% 100%",
        imageZoomDesktop: 1.0896,
        // Applied to both desktop states for a consistent look — Figma's own
        // collapsed-desktop instance is still broken in-file (negative crop width)
        // so it can't confirm the flip there, but the expanded instance's mirror is
        // deliberate (keeps him leading into the text panel) and should carry over.
        imageFlipDesktop: true,
        // Figma's collapsed-desktop instance for this persona is still broken in-file
        // (negative crop width, not recoverable) — reusing the expanded focal point
        // as a starting estimate pending visual confirmation.
        imagePositionDesktopCollapsed: "47.5% 50%",
        // This card has no zoom support on mobile — Figma's mobile crop wants a touch
        // tighter (107.29% wide) than plain object-fit:cover gives here; using the
        // plain-cover equivalent instead of adding zoom support for one persona.
        imagePositionMobile: "50% 100%",
        imagePositionMobileCollapsed: "50% 85.5%",
      },
      {
        eyebrow: "drive.persona.items.fleetManagement.eyebrow",
        title: "drive.persona.items.fleetManagement.title",
        description: "drive.persona.items.fleetManagement.description",
        ctaLabel: "drive.persona.items.learnMore",
        image: personaFleetManagement,
        imageAlt: "drive.persona.items.fleetManagement.imageAlt",
        // Figma flips this one horizontally (same mechanism as connectedTech) to keep
        // the subject leading into the text panel.
        imagePositionDesktop: "55.2% 50%",
        imageFlipDesktop: true,
        // Figma's collapsed-desktop instance for this persona is broken in-file
        // (negative crop width) — reusing the expanded focal point as a starting
        // estimate pending visual confirmation.
        imagePositionDesktopCollapsed: "55.2% 50%",
        imagePositionMobile: "50% 7.8%",
        imagePositionMobileCollapsed: "50% 58.2%",
      },
      {
        eyebrow: "drive.persona.items.rentalOperations.eyebrow",
        title: "drive.persona.items.rentalOperations.title",
        description: "drive.persona.items.rentalOperations.description",
        ctaLabel: "drive.persona.items.learnMore",
        image: personaRentalOperations,
        imageAlt: "drive.persona.items.rentalOperations.imageAlt",
        imagePositionDesktop: "48% 50%",
        // Figma's collapsed strip zooms in (144.61% of the card's height), on the handoff.
        imagePositionDesktopCollapsed: "47.6% 55.4%",
        imageZoomDesktopCollapsed: 1.4461,
        imagePositionMobile: "50% 23.3%",
        imagePositionMobileCollapsed: "50% 43.8%",
      },
      {
        eyebrow: "drive.persona.items.serviceBusiness.eyebrow",
        title: "drive.persona.items.serviceBusiness.title",
        description: "drive.persona.items.serviceBusiness.description",
        ctaLabel: "drive.persona.items.learnMore",
        image: personaServiceBusiness,
        imageAlt: "drive.persona.items.serviceBusiness.imageAlt",
        imagePositionDesktop: "28.4% 50%",
        // Figma's collapsed strip zooms in (134.79% of the card's height), bottom-anchored.
        imagePositionDesktopCollapsed: "43.1% 100%",
        imageZoomDesktopCollapsed: 1.3479,
        imagePositionMobile: "50% 49.1%",
        imagePositionMobileCollapsed: "50% 69.3%",
      },
      {
        eyebrow: "drive.persona.items.publicSector.eyebrow",
        title: "drive.persona.items.publicSector.title",
        description: "drive.persona.items.publicSector.description",
        ctaLabel: "drive.persona.items.learnMore",
        image: personaPublicSector,
        imageAlt: "drive.persona.items.publicSector.imageAlt",
        imagePositionDesktop: "28.5% 50%",
        imagePositionDesktopCollapsed: "47.3% 50%",
        // Bottom-anchored — Figma crops this photo close to the bottom edge on mobile.
        imagePositionMobile: "50% 99.9%",
        imagePositionMobileCollapsed: "50% 9.9%",
      },
    ],
  },
  reviews: {
    eyebrow: "drive.reviews.eyebrow",
    headline: "drive.reviews.headline",
    appRatings: [],
    reviews: [],
  },
  specs: {
    eyebrow: "drive.specs.eyebrow",
    headline: "drive.specs.headline",
    image: deviceExploded,
    imageAlt: "drive.specs.imageAlt",
    items: [
      { icon: "updates", label: "drive.specs.items.updates" },
      { icon: "gps", label: "drive.specs.items.gps" },
      { icon: "tamper", label: "drive.specs.items.tamper" },
      { icon: "engine", label: "drive.specs.items.engine" },
      { icon: "silent", label: "drive.specs.items.silent" },
      { icon: "accelerometer", label: "drive.specs.items.accelerometer" },
      { icon: "cellular", label: "drive.specs.items.cellular", lastInSingleColumn: true },
      { icon: "sim", label: "drive.specs.items.sim" },
    ],
  },
  faq: {
    headline: "drive.faq.headline",
    items: [
      { question: "drive.faq.items.compatibility.question", answer: "drive.faq.items.compatibility.answer" },
      { question: "drive.faq.items.install.question", answer: "drive.faq.items.install.answer" },
      { question: "drive.faq.items.subscription.question", answer: "drive.faq.items.subscription.answer" },
      { question: "drive.faq.items.location.question", answer: "drive.faq.items.location.answer" },
      { question: "drive.faq.items.sharing.question", answer: "drive.faq.items.sharing.answer" },
      { question: "drive.faq.items.history.question", answer: "drive.faq.items.history.answer" },
    ],
  },
  // Prices come from the embedded cart's live catalog, never hardcoded — see
  // ProductUpsellCard. productCode values are real, raw codes matched directly
  // against the catalog, not a friendly-name lookup through PRODUCT_CODES.
  crossSell: {
    headline: "drive.crossSell.headline",
    items: [
      {
        name: "drive.crossSell.items.view.name",
        description: "drive.crossSell.items.view.description",
        image: crossSellView,
        imageAlt: "drive.crossSell.items.view.imageAlt",
        productCode: "bounciedashcam",
        href: "/products/view",
        ctaLabel: "drive.crossSell.items.learnMore",
        ariaLabel: "drive.crossSell.items.view.ariaLabel",
      },
      {
        name: "drive.crossSell.items.go.name",
        description: "drive.crossSell.items.go.description",
        image: crossSellGo,
        imageAlt: "drive.crossSell.items.go.imageAlt",
        productCode: "bounciecharger",
        href: "/products/go",
        ctaLabel: "drive.crossSell.items.learnMore",
        ariaLabel: "drive.crossSell.items.go.ariaLabel",
      },
    ],
  },
  lastChance: {
    headline: "drive.lastChance.headline",
    ctaLabel: "drive.lastChance.ctaLabel",
    image: driveBoxDesktop,
    mobileImage: driveBoxMobile,
    imageAlt: "drive.lastChance.imageAlt",
  },
};

// Content for /products/drive/features — reuses DRIVE_PRODUCT.features' category/card
// copy, since Figma's All Features page repeats it in full.
export const DRIVE_ALL_FEATURES: ProductAllFeaturesContent = {
  metaTitle: "drive.allFeatures.metaTitle",
  metaDescription: "drive.allFeatures.metaDescription",
  headline: "drive.features.headline",
  description: "drive.allFeatures.description",
  featureDetailsHeading: "drive.allFeatures.featureDetailsHeading",
  expandAllLabel: "drive.allFeatures.expandAll",
  collapseAllLabel: "drive.allFeatures.collapseAll",
  categories: [
    { slug: "gps-routes", icon: "gps", category: featureCategory("gps_routes", 6) },
    { slug: "driving-insights", icon: "insights", category: featureCategory("driving_insights", 6) },
    { slug: "safety-security", icon: "safety", category: featureCategory("safety_security", 6) },
    { slug: "diagnostics-care", icon: "diagnostics", category: featureCategory("diagnostics_care", 6) },
    { slug: "access-users", icon: "access", category: featureCategory("access_users", 3) },
  ],
  banner: {
    headline: "drive.lastChance.headline",
    ctaLabel: "drive.allFeatures.shopCta",
    ctaHref: "/products/drive",
    image: allFeaturesShopBanner,
    imageAlt: "drive.allFeatures.shopBannerImageAlt",
  },
};
